/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/
import { useTranslationContext } from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { useEffect, useState } from "preact/hooks";
import {
  InlineNotice,
  PrimaryActionLink,
  SecondaryAction,
  VerificationCard,
} from "../components/VerificationUi.js";
import { SessionId, useSessionState } from "../hooks/session.js";

const AUTOMATIC_RETURN_DELAY_MS = 3000;
const AUTOMATIC_RETURN_DELAY_SECONDS = AUTOMATIC_RETURN_DELAY_MS / 1000;

export interface ReturnTimerDriver {
  now: () => number;
  schedule: (callback: () => void, delayMs: number) => number;
  cancel: (timer: number) => void;
}

function browserReturnTimerDriver(): ReturnTimerDriver {
  return {
    now: () => window.performance.now(),
    schedule: (callback, delayMs) => window.setTimeout(callback, delayMs),
    cancel: (timer) => window.clearTimeout(timer),
  };
}

export function scheduleAutomaticReturn(
  targetUrl: string,
  replace: (url: string) => void,
  updateCountdown: (secondsRemaining: number) => void,
  timerDriver: ReturnTimerDriver = browserReturnTimerDriver(),
): () => void {
  const deadline = timerDriver.now() + AUTOMATIC_RETURN_DELAY_MS;
  let active = true;
  let pendingTimer: number | undefined;
  let lastSecondsRemaining: number | undefined;

  const update = (): void => {
    pendingTimer = undefined;
    if (!active) return;

    const millisecondsRemaining = deadline - timerDriver.now();
    if (millisecondsRemaining <= 0) {
      active = false;
      replace(targetUrl);
      return;
    }

    const secondsRemaining = Math.ceil(millisecondsRemaining / 1000);
    if (secondsRemaining !== lastSecondsRemaining) {
      lastSecondsRemaining = secondsRemaining;
      updateCountdown(secondsRemaining);
    }

    const nextSecondBoundary =
      millisecondsRemaining - (secondsRemaining - 1) * 1000;
    pendingTimer = timerDriver.schedule(update, nextSecondBoundary);
  };

  update();
  return () => {
    active = false;
    if (pendingTimer !== undefined) {
      timerDriver.cancel(pendingTimer);
    }
  };
}

export function ChallengeCompleted({ session }: { session: SessionId }): VNode {
  const { state } = useSessionState(session);
  const { i18n } = useTranslationContext();
  const completedURL = safeRedirectURL(state?.completedURL);
  const returnURL = safeRedirectURL(session.redirectURL);
  const [secondsRemaining, setSecondsRemaining] = useState(
    AUTOMATIC_RETURN_DELAY_SECONDS,
  );
  const destination = completedURL ? new URL(completedURL).host : undefined;
  // Translators: %1$s is the host name of the application that requested
  // verification.
  const returnLabel = returnURL
    ? i18n.str`Return to ${new URL(returnURL).host}`
    : undefined;
  // Translators: %1$s is the host name of the application that requested
  // verification.
  const continueLabel = i18n.str`Continue to ${destination ?? ""}`;
  // Translators: Page title shown after Challenger has successfully verified
  // the user's email address, phone number, or postal address.
  const completeTitle = i18n.str`Verification complete`;
  // Translators: Instruction shown when Challenger cannot automatically redirect
  // the browser back to the application that requested verification.
  const manualReturnDescription = i18n.str`Return to the application that started this verification.`;
  // Translators: Confirmation text shown immediately before returning to the
  // application that requested verification.
  const continueDescription = i18n.str`Use the button below to continue immediately.`;
  // Translators: %1$s is the host name of the application that requested
  // verification. This sentence appears above a live redirect countdown.
  const redirectDescription = i18n.str`You will be redirected to ${destination} automatically.`;
  // Translators: Live countdown shown during the final second before the
  // browser returns to the application that requested verification.
  const redirectInOneSecond = i18n.str`Redirecting in 1 second…`;
  // Translators: %1$s is the live number of seconds before the browser returns
  // to the application that requested verification.
  const redirectInSeveralSeconds = i18n.str`Redirecting in ${secondsRemaining} seconds…`;

  useEffect(() => {
    if (!completedURL) return;
    return scheduleAutomaticReturn(
      completedURL,
      (url) => window.location.replace(url),
      setSecondsRemaining,
    );
  }, [completedURL]);

  if (!completedURL) {
    return (
      <VerificationCard step="complete" title={completeTitle}>
        <InlineNotice
          // Translators: "return" means a browser redirect back to the
          // application that requested verification.
          title={i18n.str`Automatic return is unavailable`}
          tone="warning"
        >
          {manualReturnDescription}
        </InlineNotice>
        {returnURL ? (
          <div class="mt-5">
            <SecondaryAction href={returnURL}>{returnLabel}</SecondaryAction>
          </div>
        ) : undefined}
      </VerificationCard>
    );
  }

  return (
    <VerificationCard
      step="complete"
      title={completeTitle}
      description={
        <span>
          <span class="block">{redirectDescription}</span>
          <span
            class="mt-1 block min-h-7 whitespace-nowrap font-semibold tabular-nums"
            aria-live="polite"
            aria-atomic="true"
          >
            {secondsRemaining === 1
              ? redirectInOneSecond
              : redirectInSeveralSeconds}
          </span>
        </span>
      }
    >
      <InlineNotice
        tone="success"
        // Translators: "Address" is generic here: it may be an email address,
        // phone number, or postal address.
        title={i18n.str`Address verified`}
      >
        {continueDescription}
      </InlineNotice>
      <div class="mt-5">
        <PrimaryActionLink href={completedURL}>
          {continueLabel}
        </PrimaryActionLink>
      </div>
    </VerificationCard>
  );
}

export function safeRedirectURL(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}
